import { useEffect, useRef, useState } from 'react';

export type AdvisorChatStatus = 'ready' | 'streaming' | 'submitted' | 'error';

export type UseAdvisorStreamIdleThreadReadyOptions = {
  chatStatus: AdvisorChatStatus;
  /** Current client thread id (ref-backed so New Chat mid-stream is visible). */
  getThreadId: () => string | null | undefined;
  markThreadServerReady: () => void;
};

function isBusyStatus(status: AdvisorChatStatus): boolean {
  return status === 'streaming' || status === 'submitted';
}

/**
 * After a successful stream settles (``submitted``/``streaming`` → ``ready``), mark the
 * active thread server-ready and bump ``streamIdleToken`` for pending-report sync.
 *
 * Skips the mark when the thread id changed while busy (New Chat / org switch + ``stop()``),
 * so a virgin replacement UUID is not treated as persisted. ``streamIdleToken`` still bumps on
 * every busy→ready settle so pending-report sync can retry after mid-stream thread switches
 * (history select / scope change) without listing ``chatStatus`` in the poll effect deps.
 *
 * Busy → ``error`` intentionally does not mark ready: avoids polling after a failed first turn
 * that may never have persisted the thread. Pending reports flush on a later successful stream.
 */
export function useAdvisorStreamIdleThreadReady(
  options: UseAdvisorStreamIdleThreadReadyOptions
): number {
  const { chatStatus, getThreadId, markThreadServerReady } = options;
  const prevChatStatusRef = useRef(chatStatus);
  const threadIdWhenBusyRef = useRef<string | null>(null);
  const getThreadIdRef = useRef(getThreadId);
  getThreadIdRef.current = getThreadId;
  const markReadyRef = useRef(markThreadServerReady);
  markReadyRef.current = markThreadServerReady;
  const [streamIdleToken, setStreamIdleToken] = useState(0);

  useEffect(() => {
    const prev = prevChatStatusRef.current;
    prevChatStatusRef.current = chatStatus;

    if (isBusyStatus(chatStatus)) {
      // Capture only on enter-busy. Refreshing while busy would adopt a New Chat virgin UUID
      // after ``beginNewAgentThread`` while ``stop()`` has not yet left streaming/submitted.
      if (!isBusyStatus(prev)) {
        threadIdWhenBusyRef.current = getThreadIdRef.current()?.trim() || null;
      }
      return;
    }

    // Only ``ready`` after busy — not ``error`` (see module docstring).
    if (isBusyStatus(prev) && chatStatus === 'ready') {
      const busyId = threadIdWhenBusyRef.current;
      threadIdWhenBusyRef.current = null;
      const currentId = getThreadIdRef.current()?.trim() || null;
      if (busyId && busyId === currentId) {
        markReadyRef.current();
      }
      // Always bump so pending-report sync retries even when we skip mark-ready.
      setStreamIdleToken(token => token + 1);
    }
  }, [chatStatus]);

  return streamIdleToken;
}
